import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HTTP_UNAUTHORIZED } from "@/lib/constants";
import { X, ShieldCheck } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function ConsentHistoryPanel({ applicationId, onClose, onUnauthorized }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!applicationId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/consent-records/${applicationId}`);
        if (!cancelled) setRecords(data.records || []);
      } catch (err) {
        if (err?.response?.status === HTTP_UNAUTHORIZED) {
          onUnauthorized?.();
          return;
        }
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [applicationId, onUnauthorized]);

  if (!applicationId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Consent history"
      data-testid="consent-history-modal"
    >
      <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col rounded-3xl bg-white border border-[#E8DFD8] shadow-2xl">
        <header className="flex items-center justify-between px-8 py-6 border-b border-[#E8DFD8]">
          <div>
            <p className="overline flex items-center gap-2">
              <ShieldCheck size={12} strokeWidth={1.5} /> Consent Audit
            </p>
            <h2 className="font-serif-lux text-2xl mt-1 text-[#2A1C14]">
              Consent history
            </h2>
            <p className="text-xs text-[#8C7A70] mt-1 font-mono">
              Application: {applicationId}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="consent-history-close"
            className="text-[#5A3B31] hover:text-[#2A1C14] p-2"
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <p className="text-sm text-[#8C7A70]">Loading records...</p>
          ) : error ? (
            <p className="text-sm text-red-600">Failed to load consent records.</p>
          ) : records.length === 0 ? (
            <p className="text-sm text-[#8C7A70]">No consent records found for this application.</p>
          ) : (
            <div className="space-y-6">
              {records.map((r, i) => (
                <article
                  key={r.consent_record_id || i}
                  className="rounded-2xl border border-[#E8DFD8] p-5"
                  data-testid={`consent-record-${i}`}
                >
                  <header className="flex items-baseline justify-between mb-3">
                    <div>
                      <p className="text-[11px] text-[#8C7A70] font-mono">
                        {r.consent_record_id}
                      </p>
                      <p className="font-serif-lux text-lg text-[#2A1C14] mt-1">
                        {r.applicant_name}
                      </p>
                      <p className="text-xs text-[#5A3B31]">
                        {r.applicant_email} · {r.applicant_phone || "no phone"}
                      </p>
                    </div>
                    <div className="text-right text-xs text-[#8C7A70]">
                      <p>UTC: {fmtDate(r.consented_at_utc)}</p>
                      <p>Local: {r.consented_at_local || "n/a"}</p>
                    </div>
                  </header>

                  <div className="grid grid-cols-2 gap-2 text-[11px] text-[#5A3B31] mt-3">
                    <p><strong>Form:</strong> {r.application_form_version}</p>
                    <p><strong>UI:</strong> {r.consent_ui_version}</p>
                    <p><strong>Source:</strong> {r.source || "website application"}</p>
                    <p><strong>IP:</strong> {r.ip_address || "not recorded"}</p>
                    <p className="col-span-2 truncate"><strong>UA:</strong> <span className="font-mono">{r.user_agent || "n/a"}</span></p>
                    {r.referring_page ? (
                      <p className="col-span-2 truncate"><strong>Referrer:</strong> {r.referring_page}</p>
                    ) : null}
                    {r.twilio_campaign_id ? (
                      <p className="col-span-2"><strong>Twilio campaign:</strong> {r.twilio_campaign_id}</p>
                    ) : null}
                  </div>

                  <div className="mt-5">
                    <p className="overline mb-2">Checkbox state</p>
                    <ul className="space-y-2">
                      {(r.consents || []).map((c, idx) => (
                        <li key={idx} className="flex gap-3 items-start">
                          <span
                            className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                              c.checked ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                            }`}
                          >
                            {c.checked ? "✓" : "✕"}
                          </span>
                          <div className="flex-1 text-xs">
                            <p className="font-semibold text-[#2A1C14]">
                              {c.key}{" "}
                              <span className="font-mono text-[10px] text-[#8C7A70] ml-1">
                                ({c.document_version})
                              </span>
                            </p>
                            <p className="text-[#5A3B31] mt-0.5">{c.wording_shown}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
